import { useState, useEffect } from 'react';
import { useAuth } from './hooks/useAuth';
import { useClients } from './hooks/useClients';
import { usePrograms } from './hooks/usePrograms';
import { useCheckins } from './hooks/useCheckins';
import { LoginForm } from './components/auth/LoginForm';
import { RegisterForm } from './components/auth/RegisterForm';
import { ClientList } from './components/clients/ClientList';
import { NewClientForm } from './components/clients/NewClientForm';
import { ProgramView } from './components/program/ProgramView';
import { GeneratingScreen } from './components/program/GeneratingScreen';
import { CheckinForm } from './components/checkin/CheckinForm';
import { CheckinHistory } from './components/checkin/CheckinHistory';
import { Button } from './components/shared';
import type { Client, CreateClientInput, CreateCheckinInput } from './types';
import styles from './App.module.css';

type View = 'auth' | 'dashboard' | 'new-client' | 'programs' | 'checkin';
type AuthMode = 'login' | 'register';

function App() {
  // Auth
  const { trainer, isAuthenticated, isLoading: authLoading, error: authError, register, login, logout, clearError } = useAuth();

  // Clients
  const { clients, isLoading: clientsLoading, error: clientsError, addClient, removeClient } = useClients();

  // Programs
  const { programs, isLoading: programsLoading, isGenerating, generate, fetchByClient } = usePrograms();

  // Checkins
  const { checkins, isLoading: checkinsLoading, error: checkinsError, submitCheckin, fetchByClient: fetchCheckins } = useCheckins();

  // Navigation state
  const [view, setView] = useState<View>('auth');
  const [authMode, setAuthMode] = useState<AuthMode>('login');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);

  // Redirect to dashboard when authenticated
  useEffect(() => {
    if (isAuthenticated && view === 'auth') {
      setView('dashboard');
    }
  }, [isAuthenticated, view]);

  // Handle client selection for programs
  const handleViewPrograms = async (client: Client) => {
    setSelectedClient(client);
    await fetchByClient(client.id);
    setView('programs');
  };

  // Handle generate program from card
  const handleGenerateProgramFromCard = async (client: Client) => {
    setSelectedClient(client);
    await fetchByClient(client.id);
    setView('programs');
    // Note: In the programs view, user will click "Generate Program" button
  };

  // Handle client selection for checkin
  const handleCheckin = async (client: Client) => {
    setSelectedClient(client);
    await fetchByClient(client.id);
    await fetchCheckins(client.id);
    setView('checkin');
  };

  // Handle program generation
  const handleGenerateProgram = async () => {
    if (!selectedClient) return;
    await generate(selectedClient.id);
  };

  // Handle new client creation
  const handleCreateClient = async (input: CreateClientInput) => {
    await addClient(input);
    setView('dashboard');
  };

  // Handle checkin submission
  const handleSubmitCheckin = async (input: CreateCheckinInput) => {
    const result = await submitCheckin(input);
    return result;
  };

  // Handle client deletion
  const handleDeleteClient = async (id: string) => {
    if (confirm('Are you sure you want to delete this client?')) {
      await removeClient(id);
    }
  };

  // Auth handlers
  const handleLogin = async (input: { email: string; password: string }) => {
    await login(input);
  };

  const handleRegister = async (input: { email: string; password: string; name: string }) => {
    await register(input);
  };

  const handleLogout = () => {
    logout();
    setView('auth');
    setAuthMode('login');
  };

  // Render auth view
  if (!isAuthenticated || view === 'auth') {
    return (
      <div className={styles.app}>
        <div className={styles.authContainer}>
          {authMode === 'login' ? (
            <LoginForm
              onSubmit={handleLogin}
              onSwitchToRegister={() => {
                setAuthMode('register');
                clearError();
              }}
              error={authError}
              isLoading={authLoading}
            />
          ) : (
            <RegisterForm
              onSubmit={handleRegister}
              onSwitchToLogin={() => {
                setAuthMode('login');
                clearError();
              }}
              error={authError}
              isLoading={authLoading}
            />
          )}
        </div>
      </div>
    );
  }

  // Show generating screen
  if (isGenerating && selectedClient) {
    return (
      <div className={styles.app}>
        <GeneratingScreen clientName={selectedClient.name} />
      </div>
    );
  }

  // Render main views
  return (
    <div className={styles.app}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.logo} onClick={() => setView('dashboard')}>
            <span className={styles.logoIcon}>💪</span>
            <span className={styles.logoText}>PTai</span>
          </div>
          <div className={styles.user}>
            <span className={styles.userName}>{trainer?.name}</span>
            <button className={styles.logoutButton} onClick={handleLogout}>
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className={styles.main}>
        {view === 'dashboard' && (
          <>
            <div className={styles.dashboardHeader}>
              <h1 className={styles.dashboardTitle}>Dashboard</h1>
              <Button onClick={() => setView('new-client')}>
                + New Client
              </Button>
            </div>
            <ClientList
              clients={clients}
              isLoading={clientsLoading}
              error={clientsError}
              onViewPrograms={handleViewPrograms}
              onCheckin={handleCheckin}
              onDelete={handleDeleteClient}
              onAddNew={() => setView('new-client')}
              onGenerateProgram={handleGenerateProgramFromCard}
            />
          </>
        )}

        {view === 'new-client' && (
          <NewClientForm
            onSubmit={handleCreateClient}
            onCancel={() => setView('dashboard')}
            isLoading={clientsLoading}
          />
        )}

        {view === 'programs' && selectedClient && (
          <ProgramView
            client={selectedClient}
            programs={programs}
            isLoading={programsLoading}
            isGenerating={isGenerating}
            onGenerate={handleGenerateProgram}
            onBack={() => setView('dashboard')}
          />
        )}

        {view === 'checkin' && selectedClient && (
          <>
            <CheckinForm
              client={selectedClient}
              programs={programs}
              onSubmit={handleSubmitCheckin}
              onCancel={() => setView('dashboard')}
              isLoading={checkinsLoading}
            />
            <CheckinHistory
              checkins={checkins}
              isLoading={checkinsLoading}
              error={checkinsError}
            />
          </>
        )}
      </main>
    </div>
  );
}

export default App;
